import { AudienceBuilder } from '../../../audience/AudienceBuilder.tsx';
import { matchesEveryone, readDecisionSplit, type DecisionSplitConfig } from './decisionSplitConfig.ts';
import './stepPanels.css';

export interface DecisionSplitPanelProps {
  environmentId: string;
  /** The step's `config` as stored. */
  value: unknown;
  /** Every edit; the ports stay `yes` and `no`. */
  onChange: (next: DecisionSplitConfig) => void;
  readOnly?: boolean;
}

/**
 * Settings of a Decision Split step (BB-125): one audience, built with the shared audience builder (BB-40). When a user
 * reaches the step their profile and the entry's context are checked against it: a match goes down Yes, anything else
 * down No — Audience Paths with one group and Everyone Else. A branch with no step after it ends the Canvas with its
 * answer as the reason. The builder's «Check a user» tells which branch a user would take now.
 */
export function DecisionSplitPanel({ environmentId, value, onChange, readOnly = false }: DecisionSplitPanelProps) {
  const config = readDecisionSplit(value);
  return (
    <section className="step-panel" aria-label="Decision Split settings">
      <p className="hint">The audience is checked once, when a user reaches this step, on their profile at that moment and this entry’s context.</p>
      <ul aria-label="Branches" className="decision-split__branches">
        <li>
          <strong>Yes</strong> — users who match the audience.
        </li>
        <li>
          <strong>No</strong> — everyone else, including users an exclusion group or app targeting leaves out.
        </li>
      </ul>
      <p className="hint">A branch with no step after it ends the Canvas for those users; reports tell the two exits apart (decision_split_yes, decision_split_no). «Check a user» below shows the branch a user would take now: a match goes down Yes.</p>
      {matchesEveryone(config.audience) && <p className="notice notice--warning">No filters yet: every user goes down Yes.</p>}
      <AudienceBuilder environmentId={environmentId} value={config.audience} readOnly={readOnly} onChange={(next) => onChange({ audience: next })} />
    </section>
  );
}
